import type { CirclesChart } from '@shared/circlesChart';
import { GOOD_WINDOW_MS, OK_WINDOW_MS, PERFECT_WINDOW_MS } from '@shared/gameTypes';
import type { HitErrorEntry } from './circlesRenderTypes';
import {
  HIT_ERROR_FADE_START,
  HIT_ERROR_HISTORY_MAX,
  HIT_ERROR_LIFE,
  HIT_ERROR_MEAN_TRIGGER,
} from './circlesRenderConstants';
import { hexToRgba } from './circlesRenderColour';

/**
 * Osu-style hit-error meter. Logs the last {@link HIT_ERROR_HISTORY_MAX}
 * tap timings and renders them as coloured ticks along a horizontal
 * strip at the bottom of the playfield. Players learn whether they're
 * consistently early or late — the single highest-leverage piece of
 * feedback in any timing game.
 *
 * The history is intentionally render-only — misses aren't recorded
 * (their `diffMs` reflects auto-resolution beat timing, not actual
 * player input) and the entries survive loop wraps so timing trends
 * carry across replays of the same chart.
 */
export class HitErrorMeter {
  /**
   * Rolling log of recent hit timings. Newest entries at the tail;
   * capped at {@link HIT_ERROR_HISTORY_MAX} so a long survival run
   * can't accumulate unbounded entries. Misses are excluded since
   * their `diffMs` is the auto-resolution beat minus the target beat,
   * which doesn't reflect actual player input timing.
   */
  private hitErrorHistory: HitErrorEntry[] = [];

  /**
   * Record a successful hit's timing. Called by the session on every
   * perfect / good / ok resolution. The buffer is FIFO-capped so a
   * long run can't grow it unbounded: old entries fall off the
   * front once the cap is reached.
   */
  record(diffMs: number, verdict: 'perfect' | 'good' | 'ok'): void {
    this.hitErrorHistory.push({ diffMs, verdict, age: 0 });
    if (this.hitErrorHistory.length > HIT_ERROR_HISTORY_MAX) {
      this.hitErrorHistory.splice(0, this.hitErrorHistory.length - HIT_ERROR_HISTORY_MAX);
    }
  }

  /** Age + cull entries. */
  update(dt: number): void {
    if (this.hitErrorHistory.length === 0) {
      return;
    }
    for (let i = this.hitErrorHistory.length - 1; i >= 0; i--) {
      const e = this.hitErrorHistory[i];
      e.age += dt;
      if (e.age >= HIT_ERROR_LIFE) {
        this.hitErrorHistory.splice(i, 1);
      }
    }
  }

  /**
   * Draw the hit-error meter at the bottom-centre of the canvas.
   *
   * Layout:
   *   • Background bar showing the full ±goodWindowMs range.
   *   • Green band: the perfect window (-perfectMs..+perfectMs).
   *   • Blue bands: the good window (between perfect edge and good edge).
   *   • White vertical centre line: zero timing offset (the "perfect" axis).
   *   • Per-entry tick: a 2px vertical line at the entry's diff position,
   *     coloured green for perfect / blue for good, fading from full
   *     opacity at age=0 down to zero at age=HIT_ERROR_LIFE.
   *   • Optional orange triangle below the bar pointing up at the
   *     recent mean — surfaces consistent early/late drift so the
   *     player can correct.
   *
   * Skipped entirely when the history is empty so a quiet HUD stays
   * quiet (no inert background bar nagging the player).
   */
  draw(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    w: number,
    h: number,
  ): void {
    if (this.hitErrorHistory.length === 0) {
      return;
    }
    const perfectMs = chart.perfectWindowMs ?? PERFECT_WINDOW_MS;
    const goodMs = chart.goodWindowMs ?? GOOD_WINDOW_MS;
    const okMs = chart.okWindowMs ?? OK_WINDOW_MS;
    // Bar is scaled to the widest active window so OK ticks don't
    // get clipped off the ends. When OK isn't in play (rare opt-out
    // chart), fall back to good.
    const outerMs = Math.max(goodMs, okMs);
    if (outerMs <= 0) {
      return;
    }

    const barW = Math.min(280, w * 0.45);
    const barH = 6;
    const barX = (w - barW) / 2;
    const barY = h - 32;
    const halfBarW = barW / 2;
    const centreX = barX + halfBarW;

    ctx.save();
    ctx.shadowBlur = 0;

    // Layered bands: OK (amber) outer, good (cyan) mid, perfect
    // (green) centre. Each is drawn full-width and then masked by
    // the next tighter colour, so colours visually nest.
    ctx.fillStyle = 'rgba(255, 216, 77, 0.16)';
    ctx.fillRect(barX, barY, barW, barH);

    const goodPxHalf = halfBarW * Math.max(0, Math.min(1, goodMs / outerMs));
    ctx.fillStyle = 'rgba(22, 224, 255, 0.22)';
    ctx.fillRect(centreX - goodPxHalf, barY, goodPxHalf * 2, barH);

    const perfectPxHalf = halfBarW * Math.max(0, Math.min(1, perfectMs / outerMs));
    ctx.fillStyle = 'rgba(59, 255, 157, 0.32)';
    ctx.fillRect(centreX - perfectPxHalf, barY, perfectPxHalf * 2, barH);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.fillRect(centreX - 0.5, barY - 2, 1, barH + 4);

    for (let i = 0; i < this.hitErrorHistory.length; i++) {
      const e = this.hitErrorHistory[i];
      const ratio = Math.max(-1, Math.min(1, e.diffMs / outerMs));
      const tickX = centreX + ratio * halfBarW;
      const ageT = ageOpacity(e.age);
      const tickColour =
        e.verdict === 'perfect' ? '#3bff9d' : e.verdict === 'good' ? '#16e0ff' : '#ffd84d';
      ctx.fillStyle = hexToRgba(tickColour, 0.85 * ageT);
      ctx.fillRect(tickX - 1, barY - 3, 2, barH + 6);
    }

    if (this.hitErrorHistory.length >= HIT_ERROR_MEAN_TRIGGER) {
      let sum = 0;
      let weight = 0;
      for (let i = 0; i < this.hitErrorHistory.length; i++) {
        const e = this.hitErrorHistory[i];
        const ageT = ageOpacity(e.age);
        sum += e.diffMs * ageT;
        weight += ageT;
      }
      if (weight > 0) {
        const mean = sum / weight;
        const ratio = Math.max(-1, Math.min(1, mean / outerMs));
        const arrowX = centreX + ratio * halfBarW;
        const arrowY = barY + barH + 1;
        ctx.fillStyle = 'rgba(255, 200, 95, 0.95)';
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(arrowX - 4, arrowY + 6);
        ctx.lineTo(arrowX + 4, arrowY + 6);
        ctx.closePath();
        ctx.fill();
      }
    }

    ctx.restore();
  }
}

/**
 * Per-entry fade envelope: holds at 1 until {@link HIT_ERROR_FADE_START},
 * then linearly ramps to 0 over the remaining life. Newer ticks stay
 * opaque while older ones dim so the eye is always drawn to current
 * performance.
 */
function ageOpacity(age: number): number {
  if (age < HIT_ERROR_FADE_START) {
    return 1;
  }
  return Math.max(0, 1 - (age - HIT_ERROR_FADE_START) / (HIT_ERROR_LIFE - HIT_ERROR_FADE_START));
}
